import * as Yup from 'yup';
import { VALIDATION_MESSAGES, REGEX_PATTERNS } from './constants';
import { isValidPhoneNumber, getRawPhoneDigits } from './phoneFormatter.js';

// Helper function for password validation
const validatePassword = (value) => {
  if (!value) return false;
  const missing = [];
  
  if (value.length < 8) missing.push('8 characters');
  if (!REGEX_PATTERNS.PASSWORD.UPPERCASE.test(value)) missing.push('uppercase');
  if (!REGEX_PATTERNS.PASSWORD.LOWERCASE.test(value)) missing.push('lowercase');
  if (!REGEX_PATTERNS.PASSWORD.DIGIT.test(value)) missing.push('number');
  if (!REGEX_PATTERNS.PASSWORD.SPECIAL.test(value)) missing.push('special character');
  
  return missing.length === 0 ? true : `${VALIDATION_MESSAGES.PASSWORD_WEAK} ${missing.join(', ')}`;
};

// Login validation schema
export const loginSchema = Yup.object({
  email: Yup.string()
    .trim()
    .email(VALIDATION_MESSAGES.EMAIL_INVALID)
    .required(VALIDATION_MESSAGES.REQUIRED),
  
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required(VALIDATION_MESSAGES.REQUIRED)
});

// Sign up validation schema
export const signUpSchema = Yup.object({
  firstName: Yup.string()
    .trim()
    .matches(REGEX_PATTERNS.NAME, VALIDATION_MESSAGES.NAME_INVALID)
    .matches(REGEX_PATTERNS.SINGLE_SPACE, "Only one space allowed between words")
    .required("First name is required"),

  lastName: Yup.string()
    .trim()
    .matches(REGEX_PATTERNS.NAME, VALIDATION_MESSAGES.NAME_INVALID)
    .matches(REGEX_PATTERNS.SINGLE_SPACE, "Only one space allowed between words")
    .required("Last name is required"),

  email: Yup.string()
    .trim()
    .email(VALIDATION_MESSAGES.EMAIL_INVALID)
    .matches(REGEX_PATTERNS.EMAIL, "Email must contain '@' and '.'")
    .required("Email is required"),

  phoneNumber: Yup.string()
    .test('phone-validation', 'Phone number must be exactly 10 digits', function(value) {
      // Allow empty values (not required)
      if (!value || value.trim() === '+1' || value.trim() === '') return true;
      return isValidPhoneNumber(value);
    })
    .test("no-same-digits", "Phone number cannot have all digits the same", 
      function(value) {
        // Allow empty values (not required)
        if (!value || value.trim() === '+1' || value.trim() === '') return true;
        const digits = getRawPhoneDigits(value || "");
        return !/^(\d)\1{9}$/.test(digits);
      }),

  address1: Yup.string()
    .trim()
    .min(5, "Address must be at least 5 characters")
    .max(100, "Address must be less than 100 characters")
    .required("Address is required"),

  // NEW: Address 2 validation (optional)
  address2: Yup.string()
    .trim()
    .max(100, "Address must be less than 100 characters")
    .nullable(),

  // State validation
  state: Yup.string()
    .required("State is required"),

  // City validation
  city: Yup.string()
    .required("City is required"),

  // NEW: Zip code validation
  zip: Yup.string()
    .trim()
    .matches(/^\d{5}$/, "Zip code must be exactly 5 digits")
    .required("Zip code is required"), 

  referralCode: Yup.string().nullable(),
  
  termsAccepted: Yup.boolean()
    .oneOf([true], "Please accept Terms and Conditions")
});

// Create password validation schema
export const createPasswordSchema = Yup.object({
  newPassword: Yup.string()
    .required(VALIDATION_MESSAGES.REQUIRED)
    .test('password-strength', function(value) {
      const result = validatePassword(value);
      return result === true ? true : this.createError({ message: result });
    }),

  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('newPassword')], VALIDATION_MESSAGES.PASSWORDS_NO_MATCH)
});

// Forgot password validation schema
export const forgotPasswordSchema = Yup.object({
  email: Yup.string()
    .trim()
    .email(VALIDATION_MESSAGES.EMAIL_INVALID)
    .required(VALIDATION_MESSAGES.REQUIRED)
});

// Legacy contact step 1 validation schema with complete address fields
export const legacyContactStep1Schema = Yup.object({
  primaryContactName: Yup.string()
    .trim()
    .matches(/^[a-zA-Z\s]+$/, 'Only letters and spaces are allowed')
    .matches(/^\S+(?: \S+)*$/, "Only one space allowed between words")
    .required("Primary Legacy Contact Name is required"),

  primaryEmail: Yup.string()
    .trim()
    .email('Please enter a valid email address')
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Email must contain '@' and '.'")
    .required("Email is required"),

  primaryPhone: Yup.string()
    .test('phone-validation', 'Phone number must be exactly 10 digits', function(value) {
      return isValidPhoneNumber(value);
    })
    .required("Phone number is required"),

  primaryRelationship: Yup.string()
    .trim()
    .matches(/^[a-zA-Z\s]+$/, 'Only letters and spaces are allowed')
    .matches(/^\S+(?: \S+)*$/, "Only one space allowed between words")
    .required("Relationship is required"),

  primaryAddress1: Yup.string()
    .trim()
    .required("Address is required"),

  primaryAddress2: Yup.string().nullable(),

  primaryState: Yup.string()
    .required("State is required"),

  primaryCity: Yup.string()
    .required("City is required"),

  primaryZip: Yup.string()
    .matches(/^\d{5}(\d{4})?$/, 'Zip code must be 5 or 9 digits')
    .required("Zip code is required"),

  showSecondary: Yup.boolean(),

  secondaryContactName: Yup.string().when('showSecondary', {
    is: true,
    then: (schema) => schema
      .trim()
      .matches(/^[a-zA-Z\s]+$/, 'Only letters and spaces are allowed')
      .matches(/^\S+(?: \S+)*$/, "Only one space allowed between words")
      .required("Secondary Legacy Contact Name is required"),
    otherwise: (schema) => schema.nullable()
  }),

  secondaryEmail: Yup.string().when('showSecondary', {
    is: true,
    then: (schema) => schema
      .trim()
      .email('Please enter a valid email address')
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Email must contain '@' and '.'")
      .required("Email is required")
      .test('different-from-primary', 
        'Secondary email must be different from primary email',
        function(value) {
          const { primaryEmail } = this.parent;
          if (!value || !primaryEmail) return true;
          return value.toLowerCase() !== primaryEmail.toLowerCase();
        }
      ),
    otherwise: (schema) => schema.nullable()
  }),

  secondaryPhone: Yup.string().when('showSecondary', {
    is: true,
    then: (schema) => schema
      .test('phone-validation', 'Phone number must be exactly 10 digits', function(value) {
        return isValidPhoneNumber(value);
      })
      .test('different-from-primary', 
        'Secondary phone number must be different from primary phone number',
        function(value) {
          const { primaryPhone } = this.parent;
          if (!value || !primaryPhone) return true;
          const primaryDigits = getRawPhoneDigits(primaryPhone);
          const secondaryDigits = getRawPhoneDigits(value);
          return primaryDigits !== secondaryDigits;
        }
      )
      .required("Phone number is required"),
    otherwise: (schema) => schema.nullable()
  }),

  secondaryRelationship: Yup.string().when('showSecondary', {
    is: true,
    then: (schema) => schema
      .trim()
      .matches(/^[a-zA-Z\s]+$/, 'Only letters and spaces are allowed')
      .matches(/^\S+(?: \S+)*$/, "Only one space allowed between words")
      .required("Relationship is required"),
    otherwise: (schema) => schema.nullable()
  }),

  secondaryAddress1: Yup.string().when('showSecondary', {
    is: true,
    then: (schema) => schema
      .trim()
      .required("Address is required"),
    otherwise: (schema) => schema.nullable()
  }),

  secondaryAddress2: Yup.string().nullable(),

  secondaryState: Yup.string().when('showSecondary', {
    is: true,
    then: (schema) => schema.required("State is required"),
    otherwise: (schema) => schema.nullable()
  }),

  secondaryCity: Yup.string().when('showSecondary', {
    is: true,
    then: (schema) => schema.required("City is required"),
    otherwise: (schema) => schema.nullable()
  }),

  secondaryZip: Yup.string().when('showSecondary', {
    is: true,
    then: (schema) => schema
      .matches(/^\d{5}(\d{4})?$/, 'Zip code must be 5 or 9 digits')
      .required("Zip code is required"),
    otherwise: (schema) => schema.nullable()
  })
});

// Legacy contact step 2 validation schema
export const legacyContactStep2Schema = Yup.object({
  hearAbout: Yup.string()
    .required("Please select an option")
});